import { useState, useMemo, useEffect } from "react";
import { KeyRound, Users, ShieldCheck, GraduationCap, Award, Trash2, Loader2, Search, Plus, Link as LinkIcon, Copy, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import toast from "react-hot-toast";
import { usePeriods } from "@/services/usePeriods";
import {
    useAdminMainroomAttendances,
    useDeleteMainroomAttendance,
    useAdminMainroomAttendanceCodes,
    useCreateMainroomAttendanceCode,
    useDeactivateMainroomAttendanceCode,
    exportMainroomAttendancesExcel,
} from "@/services/adminMainroomAttendanceService";

const CATEGORY_LABEL: Record<string, string> = {
    vip: "VIP",
    registered: "Peserta Terdaftar",
    other_invitee: "Undangan Lain",
};

export default function MainroomAttendancePage() {
    const { data: periods } = usePeriods();
    const activePeriod = periods?.find((p: any) => p.is_active);

    const [filterPeriod, setFilterPeriod] = useState<string>("all");
    const [filterCategory, setFilterCategory] = useState<string>("all");
    const [filterAttendanceCode, setFilterAttendanceCode] = useState<string>("all");
    const [filterSearch, setFilterSearch] = useState<string>("");
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [newCodeLabel, setNewCodeLabel] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    useEffect(() => {
        if (activePeriod && filterPeriod === "all") {
            setFilterPeriod(String(activePeriod.period_id));
        }
    }, [activePeriod, filterPeriod]);

    const { data: attendances, isLoading } = useAdminMainroomAttendances({
        period_id: filterPeriod !== "all" ? filterPeriod : undefined,
        category: filterCategory,
        attendance_code_id: filterAttendanceCode !== "all" ? filterAttendanceCode : undefined,
        search: filterSearch || undefined,
    });

    const { data: codes } = useAdminMainroomAttendanceCodes(filterPeriod !== "all" ? filterPeriod : undefined);
    const activeCode = codes?.find((c) => c.is_active);

    const deleteAttendance = useDeleteMainroomAttendance();
    const createCode = useCreateMainroomAttendanceCode();
    const deactivateCode = useDeactivateMainroomAttendanceCode();

    const stats = useMemo(() => {
        if (!attendances) return { total: 0, vip: 0, registered: 0, other: 0, eligible: 0 };
        return {
            total: attendances.length,
            vip: attendances.filter((a) => a.category === "vip").length,
            registered: attendances.filter((a) => a.category === "registered").length,
            other: attendances.filter((a) => a.category === "other_invitee").length,
            eligible: attendances.filter((a) => a.eligible_for_certificate).length,
        };
    }, [attendances]);

    const totalItems = attendances?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const paginatedAttendances = attendances?.slice(startIndex, endIndex) ?? [];

    useEffect(() => {
        setPage(1);
    }, [filterPeriod, filterCategory, filterAttendanceCode, filterSearch]);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await exportMainroomAttendancesExcel({
                period_id: filterPeriod !== "all" ? filterPeriod : undefined,
                category: filterCategory !== "all" ? filterCategory : undefined,
                attendance_code_id: filterAttendanceCode !== "all" ? filterAttendanceCode : undefined,
                search: filterSearch || undefined,
            });
            toast.success("Data berhasil diexport!");
        } catch {
            toast.error("Gagal melakukan export data");
        } finally {
            setIsExporting(false);
        }
    };

    const handleCreateCode = () => {
        if (!newCode.trim()) {
            toast.error("Kode presensi wajib diisi.");
            return;
        }
        if (filterPeriod === "all") {
            toast.error("Pilih periode terlebih dahulu.");
            return;
        }
        createCode.mutate(
            { period_id: Number(filterPeriod), code: newCode.trim().toUpperCase(), label: newCodeLabel || undefined },
            {
                onSuccess: () => {
                    toast.success("Kode presensi baru berhasil diaktifkan.");
                    setIsCodeModalOpen(false);
                    setNewCode("");
                    setNewCodeLabel("");
                },
                onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal membuat kode presensi."),
            }
        );
    };

    const attendanceUrl = `${window.location.origin}/mainroom-attendance`;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6">
                <div className="flex items-start gap-5">
                    <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                        <Users className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Presensi Mainroom / Plenary Session
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola kode presensi dan lihat data kehadiran peserta di sesi utama (mainroom).
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button
                        onClick={handleExport}
                        disabled={isExporting}
                        variant="outline"
                        className="h-12 px-6 rounded-xl border-slate-200 dark:border-slate-700 font-semibold"
                    >
                        {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Export Excel
                    </Button>
                    <Button onClick={() => setIsCodeModalOpen(true)} className="h-12 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-600/20">
                        <Plus className="w-5 h-5 mr-2" />
                        Buat Kode Presensi Baru
                    </Button>
                </div>
            </div>

            {/* Link & Active Code Card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                        <LinkIcon className="w-4 h-4 text-indigo-500" /> Link Form Presensi Publik
                    </p>
                    <div className="flex items-center gap-2">
                        <Input readOnly value={attendanceUrl} className="h-10 rounded-lg text-sm bg-slate-50 dark:bg-slate-800" />
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            onClick={() => {
                                navigator.clipboard.writeText(attendanceUrl);
                                toast.success("Link disalin!");
                            }}
                        >
                            <Copy className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                        <KeyRound className="w-4 h-4 text-emerald-500" /> Kode Presensi Aktif
                    </p>
                    {activeCode ? (
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-2xl font-black text-slate-900 dark:text-white tracking-widest">{activeCode.code}</p>
                                {activeCode.label && <p className="text-xs text-slate-500">{activeCode.label}</p>}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => deactivateCode.mutate(activeCode.id, {
                                    onSuccess: () => toast.success("Kode presensi dinonaktifkan."),
                                    onError: () => toast.error("Gagal menonaktifkan kode."),
                                })}
                            >
                                Nonaktifkan
                            </Button>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400 italic">Belum ada kode presensi aktif untuk periode ini.</p>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                    { label: "Total Presensi", value: stats.total, icon: Users, color: "indigo" },
                    { label: "VIP", value: stats.vip, icon: ShieldCheck, color: "amber" },
                    { label: "Peserta Terdaftar", value: stats.registered, icon: Users, color: "emerald" },
                    { label: "Undangan Lain", value: stats.other, icon: GraduationCap, color: "slate" },
                    { label: "Berhak Sertifikat", value: stats.eligible, icon: Award, color: "purple" },
                ].map((s) => (
                    <div key={s.label} className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                            <s.icon className="w-3.5 h-3.5" /> {s.label}
                        </p>
                        <p className="text-2xl font-black text-slate-900 dark:text-white">{s.value}</p>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                        <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                            <SelectValue placeholder="Pilih Periode" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            <SelectItem value="all">Semua Periode</SelectItem>
                            {periods?.map((period: any) => (
                                <SelectItem key={period.period_id} value={String(period.period_id)}>
                                    {period.name} {period.is_active && "(Aktif)"}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                            <SelectValue placeholder="Semua Kategori" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            <SelectItem value="all">Semua Kategori</SelectItem>
                            <SelectItem value="vip">VIP</SelectItem>
                            <SelectItem value="registered">Peserta Terdaftar</SelectItem>
                            <SelectItem value="other_invitee">Undangan Lain</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterAttendanceCode} onValueChange={setFilterAttendanceCode}>
                        <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                            <SelectValue placeholder="Semua Kode Presensi" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            <SelectItem value="all">Semua Kode Presensi</SelectItem>
                            {codes?.map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>
                                    {c.code}{c.label ? ` - ${c.label}` : ""}{c.is_active ? " (Aktif)" : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="relative w-full">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari nama, email, institusi..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            className="pl-10 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl w-full"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                                <th className="px-6 py-4 font-semibold">Nama</th>
                                <th className="px-6 py-4 font-semibold">Kategori</th>
                                <th className="px-6 py-4 font-semibold">Institusi</th>
                                <th className="px-6 py-4 font-semibold">Kode Presensi</th>
                                <th className="px-6 py-4 font-semibold text-center">Sertifikat</th>
                                <th className="px-6 py-4 font-semibold text-right w-20">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-500" />
                                        Memuat data presensi...
                                    </td>
                                </tr>
                            ) : totalItems === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                        Belum ada data presensi.
                                    </td>
                                </tr>
                            ) : (
                                paginatedAttendances.map((att, idx) => (
                                    <tr key={att.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                        <td className="px-6 py-4 text-center text-slate-500">{startIndex + idx + 1}</td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-900 dark:text-white">{att.full_name}</div>
                                            <div className="text-xs text-slate-500">{att.email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                {CATEGORY_LABEL[att.category] || att.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">{att.institution || "-"}</td>
                                        <td className="px-6 py-4 text-sm">
                                            {att.attendance_code ? (
                                                <div>
                                                    <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">{att.attendance_code.code}</span>
                                                    {att.attendance_code.label && (
                                                        <div className="text-xs text-slate-400">{att.attendance_code.label}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {att.eligible_for_certificate ? (
                                                <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                                    Berhak
                                                </span>
                                            ) : (
                                                <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                                    Tidak
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setDeleteConfirmId(att.id)}
                                                className="h-8 w-8 text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalItems > 0 && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Buat Kode */}
            <Dialog open={isCodeModalOpen} onOpenChange={setIsCodeModalOpen}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Buat Kode Presensi Baru</DialogTitle>
                        <DialogDescription>
                            Kode lama (jika ada) untuk periode ini akan otomatis dinonaktifkan.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Kode Presensi</Label>
                            <Input
                                value={newCode}
                                onChange={(e) => setNewCode(e.target.value)}
                                placeholder="Contoh: MR001"
                                className="h-11 rounded-xl uppercase"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Label (Opsional)</Label>
                            <Input
                                value={newCodeLabel}
                                onChange={(e) => setNewCodeLabel(e.target.value)}
                                placeholder="Contoh: Hari 1 - Sesi Pagi"
                                className="h-11 rounded-xl"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCodeModalOpen(false)}>Batal</Button>
                        <Button onClick={handleCreateCode} disabled={createCode.isPending} className="bg-indigo-600 hover:bg-indigo-700">
                            {createCode.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Aktifkan Kode
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Modal */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Data Presensi?</DialogTitle>
                        <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button
                            variant="destructive"
                            disabled={deleteAttendance.isPending}
                            onClick={async () => {
                                if (!deleteConfirmId) return;
                                try {
                                    await deleteAttendance.mutateAsync(deleteConfirmId);
                                    toast.success("Data presensi berhasil dihapus.");
                                    setDeleteConfirmId(null);
                                } catch {
                                    toast.error("Gagal menghapus data presensi.");
                                }
                            }}
                        >
                            {deleteAttendance.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
